/**
 * `ordering` — put these in the right sequence.
 *
 * Added because two of the first real generated assets wanted it: a memory game shipped
 * ungraded, and an image-ordering activity encoded its answer as a `textEntry` letter
 * string. That works and scores, but it is all-or-nothing and a gradebook holding
 * `"CADB"` cannot ask which position learners get wrong.
 *
 * The comparison is position by position. What the learner *did* to arrive at the order —
 * drag, up/down buttons, a sortable list — is entirely the asset's business.
 */

import { isOrderingResponse } from '@cet/asset-contract';
import type { ItemResponse, ItemVerdict, Locale, OrderingSchemeDoc, VerdictPart } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { graded, unanswered } from '../verdicts.js';

export function scoreOrdering(
  itemId: string,
  scheme: OrderingSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isOrderingResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is an ordering item, so its response must be { order: string[] }. ` +
        `Received keys: ${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  // An empty sequence is a blank, not a wrong answer (D32) — the learner arranged nothing.
  if (response.order.length === 0) {
    return unanswered(itemId, scheme.points);
  }

  const length = scheme.correct.length;
  let inPlace = 0;
  /**
   * Keyed by the id the learner sees in that position, not by the position number: an asset
   * marks the row it drew, and it drew rows of choices. A part reported as "position 2 is wrong"
   * would make every asset do the same lookup.
   */
  const parts: VerdictPart[] = [];
  for (let index = 0; index < length; index += 1) {
    const placed = response.order[index];
    const right = placed === scheme.correct[index];
    if (right) inPlace += 1;
    // A position the learner left short is not a part they can be shown — there is no row.
    if (placed !== undefined) parts.push({ id: placed, correct: right });
  }

  // Extra or missing entries mean the sequence is not the right one even if every position
  // that exists happens to match — a five-item answer to a four-item question is wrong.
  const whollyCorrect = inPlace === length && response.order.length === length;

  const awarded =
    (scheme.credit ?? 'all') === 'partial'
      ? // Integer arithmetic: scores never accumulate as floats, so this floors rather
        // than rounding, and a wholly correct answer gets the full points exactly.
        Math.floor((scheme.points * inPlace) / length)
      : whollyCorrect
        ? scheme.points
        : 0;

  return graded(
    itemId,
    { awarded: whollyCorrect ? scheme.points : awarded, max: scheme.points },
    scheme.feedback,
    whollyCorrect,
    locale,
    parts,
  );
}
